import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { CatalogView } from '@lemoncloud/codes-goods-api';
import { CardView } from '@lemoncloud/lemon-payments-api';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useProductService } from '@web/app/features/home/hooks';
import {
    CardRegistrationForm,
    CatalogImageSlider,
    Editor,
    OrderProductButton,
    OrderProductDialog,
    PaymentConfirmDialog,
    SectionHeader,
    SettingsSection,
    useSelectedStore,
    useStereoIcon,
} from '@web/app/shared';
import { PageContainer } from '@web/app/shared/components/PageContainer';
import { YooEditor, createYooptaEditor } from '@yoopta/editor';
import { markdown } from '@yoopta/exports';
import {
    AlertTriangle,
    Box,
    ChevronLeft,
    ChevronRight,
    Github,
    Layers,
    RefreshCw,
    Server,
    Settings,
    X,
} from 'lucide-react';

import { useFetchCatalogById } from '@codes/catalog';
import { useCards } from '@codes/payments';
import { getCurrencySymbol } from '@codes/react-utils';
import {
    Avatar,
    AvatarImage,
    Badge,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Sheet,
    SheetClose,
    SheetContent,
    SheetTitle,
    SheetTrigger,
    Skeleton,
} from '@codes/ui-kit';

import { DependencyList } from '../../components';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY);

export const CatalogDetailPage = () => {
    const { catalogId } = useParams<{ catalogId: string }>();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [isOrderDialogOpen, setIsOrderDialogOpen] = React.useState(false);
    const [isCardRegistrationOpen, setIsCardRegistrationOpen] = React.useState(false);
    const [isPaymentConfirmOpen, setIsPaymentConfirmOpen] = React.useState(false);
    const getStereoIcon = useStereoIcon();

    const { data: cardsData } = useCards({ stereo: 'stripe', view: 'mine' });
    const { workspace } = useSelectedStore();

    const {
        data: currentCatalog,
        isFetching: isCatalogFetching,
        error: fetchCatalogError,
        refetch: refetchCatalog,
        isRefetching: isCatalogRefetching,
    } = useFetchCatalogById(catalogId!);

    const yooptaEditor = useMemo(() => createYooptaEditor(), []);
    const yooptaEnEditor = useMemo(() => createYooptaEditor(), []);

    const { myProducts } = useProductService();

    useEffect(() => {
        const deserializeMarkdown = (editor: YooEditor, markdownString: string) => {
            const value = markdown.deserialize(editor, markdownString);
            editor.setEditorValue(value);
        };

        if (!currentCatalog) {
            return;
        }

        if (currentCatalog.readme) {
            deserializeMarkdown(yooptaEditor, currentCatalog.readme);
        }

        if (currentCatalog.readmeEn) {
            deserializeMarkdown(yooptaEnEditor, currentCatalog.readmeEn);
        }
    }, [currentCatalog, yooptaEditor, yooptaEnEditor]);

    const textRef = useRef<HTMLParagraphElement>(null);
    const noticeRef = useRef<HTMLUListElement>(null);

    const contentList = [t('guide.callbackNotice'), t('guide.approvalNotice'), t('guide.cancellationNotice')];

    const handleAddServiceClick = () => {
        if (!workspace) {
            return;
        }

        const hasDuplicateProductRequest = !!myProducts.find(product => product.catalogId === currentCatalog?.id);

        if (hasDuplicateProductRequest) {
            toast({ title: t('serviceRequest.duplicatedErrorMsgText') });
            return;
        }

        if (workspace.stereo === 'basic') {
            setIsOrderDialogOpen(true);
            return;
        }

        const hasNoCard = cardsData?.total === 0;

        if (hasNoCard) {
            setIsPaymentConfirmOpen(true);
            return;
        }

        const hasNoSecret = !workspace?.secret$ || !workspace?.secret$?.id;

        if (hasNoSecret) {
            // TODO: 모달창 퍼블리싱 후 처리 예정
            alert('Access key is not registered.');
            return;
        }

        setIsOrderDialogOpen(true);
    };

    const getIconImage = (catalog: CatalogView | undefined) => {
        if (!catalog) {
            return '/assets/icons/empty.svg';
        }
        const thumb = catalog.thumbs && catalog.thumbs.length >= 1 ? catalog.thumbs[0] : null;

        if (thumb) {
            return thumb;
        }

        return getStereoIcon(catalog.stereo as string);
    };

    if (!catalogId) {
        return (
            <div className="h-[60vh] flex flex-col justify-center items-center gap-3">
                <Box className="w-12 h-12 text-placeholder" />
                <div className="text-sub-title text-lg">{t('serviceDetail.empty.title')}</div>
                <p className="text-sub">{t('serviceDetail.empty.description')}</p>
                <Button onClick={() => navigate(-1)} className="gap-2">
                    <ChevronLeft className="w-4 h-4" />
                    {t('common.back')}
                </Button>
            </div>
        );
    }

    if (isCatalogFetching || isCatalogRefetching) {
        return (
            <PageContainer maxWidth="lg" className="pb-24">
                <div className="pt-12 pb-10 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10 items-start">
                    <Skeleton className="w-full h-64 rounded-xl" />
                    <div className="flex flex-col gap-4">
                        <div className="flex items-center gap-3">
                            <Skeleton className="w-12 h-12 rounded-full" />
                            <div className="flex flex-col flex-1 gap-2">
                                <Skeleton className="h-6 w-40" />
                                <Skeleton className="h-4 w-24" />
                            </div>
                        </div>
                        <Skeleton className="h-4 w-full" />
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-40 w-full rounded-2xl" />
                        <Skeleton className="h-24 w-full rounded-xl" />
                    </div>
                </div>
                <div className="h-px bg-divider mb-10" />
                <div className="space-y-4">
                    <Skeleton className="h-6 w-40" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-5/6" />
                    <Skeleton className="h-4 w-4/5" />
                </div>
            </PageContainer>
        );
    }

    if (fetchCatalogError) {
        return (
            <div className="h-[60vh] flex flex-col justify-center items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center">
                    <AlertTriangle className="w-8 h-8 text-error" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-medium text-title mb-2">{t('serviceDetail.error.loadFailed')}</h3>
                    <p className="text-sub">{t('serviceDetail.error.tryAgain')}</p>
                </div>
                <Button onClick={() => refetchCatalog()} disabled={isCatalogRefetching} className="gap-2">
                    <RefreshCw className={`w-4 h-4 ${isCatalogRefetching ? 'animate-spin' : ''}`} />
                    {t('serviceDetail.error.tryAgain')}
                </Button>
            </div>
        );
    }

    const localizedName = (i18n.language === 'en' && currentCatalog?.nameEn) || currentCatalog?.name;
    const localizedSummary = (i18n.language === 'en' && currentCatalog?.summaryEn) || currentCatalog?.summary;
    const localizedDesc = (i18n.language === 'en' && currentCatalog?.descEn) || currentCatalog?.desc;
    const hasReadme = !!(currentCatalog?.readme || currentCatalog?.readmeEn);
    const hasSettings = currentCatalog?.setting$$ && currentCatalog.setting$$.length > 0;
    const currencySymbol = currentCatalog?.price$ ? getCurrencySymbol(currentCatalog.price$.currency) : '';

    return (
        <>
            <PageContainer maxWidth="lg" className="pb-24">
                {/* Hero: 2-column on desktop */}
                <div className="pt-12 pb-10 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10 items-start">
                    {/* Left: Image */}
                    <div>
                        <CatalogImageSlider
                            images={currentCatalog?.images}
                            name={currentCatalog?.name}
                            stereo={currentCatalog?.stereo}
                            iconImage={getIconImage(currentCatalog as CatalogView)}
                        />
                    </div>

                    {/* Right: Info + Price + Guidelines */}
                    <div className="flex flex-col gap-6">
                        {/* Product Info */}
                        <div>
                            <div className="flex items-center gap-3 mb-3">
                                <Avatar className="w-12 h-12 border-2 border-point/20 shadow-sm">
                                    <AvatarImage
                                        src={getIconImage(currentCatalog as CatalogView)}
                                        alt={currentCatalog?.stereo}
                                    />
                                </Avatar>
                                <div>
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <h1 className="text-2xl font-bold text-title tracking-tight">
                                            {localizedName}
                                        </h1>
                                        <Badge className="text-[11px] h-5 px-2">
                                            {t(
                                                currentCatalog?.isDomestic
                                                    ? 'serviceDetail.domestic'
                                                    : 'serviceDetail.global'
                                            )}
                                        </Badge>
                                    </div>
                                    {currentCatalog?.stereo && (
                                        <p className="text-sm text-sub-title">{currentCatalog.stereo}</p>
                                    )}
                                </div>
                                {currentCatalog?.github$?.url && (
                                    <a
                                        href={currentCatalog.github$.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ml-auto flex items-center justify-center w-8 h-8 rounded-full bg-primary transition-all duration-200 hover:scale-110 group"
                                        title="View on GitHub"
                                    >
                                        <Github className="w-4 h-4 text-white transition-transform duration-200 group-hover:rotate-12" />
                                    </a>
                                )}
                            </div>
                            <p className="text-sm text-sub leading-relaxed">{localizedSummary}</p>
                        </div>

                        {/* Price Card */}
                        {currentCatalog?.price$ ? (
                            <div className="rounded-2xl bg-point/5 border border-point/15 p-5">
                                <div className="text-xs font-medium text-point uppercase tracking-wider mb-1">
                                    {t('price.service.initial_setup_fee')}
                                </div>
                                <div className="text-3xl font-bold text-title tracking-tight mb-4">
                                    {currencySymbol}
                                    {currentCatalog.price$.initPrice}
                                </div>
                                <div className="space-y-1.5 border-t border-point/10 pt-3">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-sub-title">
                                            {t('price.service.monthly_subscription_fee')}
                                        </span>
                                        <span className="text-sub font-medium">{currencySymbol}-</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-sub-title">{t('price.service.traffic_cost')}</span>
                                        <span className="text-sub font-medium">
                                            {currencySymbol}
                                            {currentCatalog.price$.trafficPrice}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-sub-title">{t('price.service.cost_per_hour')}</span>
                                        <span className="text-sub font-medium">
                                            {currencySymbol}
                                            {currentCatalog.price$.hourPerPrice}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-sub-title">{t('price.service.per_call')}</span>
                                        <span className="text-sub font-medium">
                                            {currencySymbol}
                                            {currentCatalog.price$.callPerPrice}
                                        </span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-sub-title">{t('price.service.usage_fee')}</span>
                                        <span className="text-sub font-medium">{currencySymbol}-</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="rounded-2xl bg-box border border-detail-line p-5 text-center text-sub">
                                {t('serviceDetail.noPrice')}
                            </div>
                        )}
                    </div>
                </div>

                {/* Divider */}
                <div className="h-px bg-divider mb-10" />

                {/* Package Deal */}
                {currentCatalog?.dependency$$ && currentCatalog.dependency$$.length > 0 && (
                    <section className="mb-10">
                        <div className="flex items-center justify-between mb-5">
                            <SectionHeader icon={Layers} title="Package Deal" />
                            {currentCatalog.dependency$$.length > 4 && (
                                <Sheet>
                                    <SheetTrigger asChild>
                                        <button className="text-xs flex items-center text-sub hover:text-point transition-colors">
                                            View All <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </SheetTrigger>
                                    <SheetContent className="w-[500px] max-md:w-full overflow-auto">
                                        <div className="flex justify-between mb-4">
                                            <SheetTitle>Package Deal View All</SheetTitle>
                                            <SheetClose asChild>
                                                <button className="w-7 h-7 flex items-center justify-center rounded-full bg-box">
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </SheetClose>
                                        </div>
                                        <DependencyList
                                            dependencies={currentCatalog.dependency$$}
                                            getIconImage={getIconImage}
                                        />
                                    </SheetContent>
                                </Sheet>
                            )}
                        </div>
                        <DependencyList
                            dependencies={currentCatalog.dependency$$}
                            sliceIdx={4}
                            getIconImage={getIconImage}
                        />
                    </section>
                )}

                {/* Divider */}
                {currentCatalog?.dependency$$ && currentCatalog.dependency$$.length > 0 && (
                    <div className="h-px bg-divider mb-10" />
                )}

                {/* Overview */}
                {localizedDesc && (
                    <section className="mb-10">
                        <SectionHeader icon={Layers} title={t('serviceDetail.overview')} />
                        <p ref={textRef} className="text-sub leading-relaxed max-w-3xl">
                            {localizedDesc}
                        </p>
                    </section>
                )}

                {/* README */}
                {(() => {
                    const isEnglish = i18n.language === 'en';
                    const hasEnglishReadme = !!currentCatalog?.readmeEn;

                    if (isEnglish && hasEnglishReadme) {
                        return (
                            <section className="mb-10">
                                <SectionHeader icon={Box} title="README" />
                                <div className="rounded-xl border border-detail-line bg-card p-6">
                                    <Editor editor={yooptaEnEditor} readOnly={true} />
                                </div>
                            </section>
                        );
                    }

                    if (currentCatalog?.readme) {
                        return (
                            <section className="mb-10">
                                <SectionHeader icon={Box} title="README" />
                                <div className="rounded-xl border border-detail-line bg-card p-6">
                                    <Editor editor={yooptaEditor} readOnly={true} />
                                </div>
                            </section>
                        );
                    }

                    return null;
                })()}

                {/* Settings */}
                {hasSettings && (
                    <section className="mb-10">
                        <SectionHeader icon={Settings} title="Settings" />
                        <SettingsSection settings={currentCatalog.setting$$!} scope="environ" icon={Server} />
                        <SettingsSection settings={currentCatalog.setting$$!} scope="config" icon={Settings} />
                    </section>
                )}

                {/* Service Guidelines */}
                <section className="mb-10">
                    <div className="rounded-xl bg-box border border-detail-line p-4">
                        <div className="text-xs font-medium text-sub-title mb-2">
                            {t('serviceDetail.serviceGuidelines')}
                        </div>
                        <ul ref={noticeRef} className="flex flex-col gap-1.5">
                            {contentList.map((content, index) => (
                                <li
                                    key={index}
                                    className="flex items-start gap-2 text-xs text-text-400 leading-relaxed"
                                >
                                    <span className="mt-1.5 w-1 h-1 rounded-full bg-text-400 shrink-0" />
                                    {content}
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            </PageContainer>

            {/* Fixed bottom button */}
            <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-divider p-4 z-50">
                <div className="max-w-6xl mx-auto px-8 max-md:px-4">
                    <div className="max-w-[500px] mx-auto">
                        <OrderProductButton onAddServiceClick={handleAddServiceClick} />
                    </div>
                </div>
            </div>

            <OrderProductDialog
                catalog={currentCatalog}
                isOpen={isOrderDialogOpen}
                onOpenChange={setIsOrderDialogOpen}
            />

            <PaymentConfirmDialog
                isOpen={isPaymentConfirmOpen}
                onOpenChange={setIsPaymentConfirmOpen}
                onConfirm={() => setIsCardRegistrationOpen(true)}
            />

            <Dialog open={isCardRegistrationOpen} onOpenChange={setIsCardRegistrationOpen}>
                <DialogContent className="max-w-[500px] px-6">
                    <DialogTitle className="mb-7 text-lg text-title">{t('myPage.registerCard')}</DialogTitle>
                    <Elements stripe={stripePromise}>
                        <CardRegistrationForm
                            onSuccess={(createdCard: CardView) => {
                                // TODO: do something
                                setIsCardRegistrationOpen(false);
                                setIsOrderDialogOpen(true);
                            }}
                        />
                    </Elements>
                </DialogContent>
            </Dialog>
        </>
    );
};
